// CDF view push-down.
//
// `partitionFilter` splits an s-expression filter predicate into a
// CDF-pushable subtree (forwarded to CDF's instance-query filter language)
// and a residual subtree (applied client-side via a synthetic `filter`
// PlanNode on the produced `LazyDataset`). `translatePredicateToCdf`
// converts a pushable subtree to the CDF filter JSON shape consumed by
// `cogniteClient.instances.query()`.
//
// Pushability and translation are ONE pure function: `compileNode` maps a
// predicate AST to its CDF filter JSON, or `undefined` when no part of CDF's
// filter language expresses it. "Pushable" is simply "compiles to a value";
// translation is the same compile with an invariant assert. There is no
// second walk to keep in sync.
//
// A leaf compiles as the product of two independent facts:
//   - the OP (`=`, `in`, `prefix`, `<`, `is-null`, …) determines the DMS
//     verb it folds to AND which value domains it is valid over. Ops live in
//     grouped builder tables — `equalityLeaf` (string scalars AND opaque
//     instance refs), `orderedLeaf` (string scalars only), `existenceLeaf`
//     (unary). Table membership IS the op's arity-and-domain; there is no
//     separate metadata to drift.
//   - the LHS classifies to a `LeafSite` — a value carrying the resolved DMS
//     property, the value domain it admits, and how to wrap the emitted leaf
//     (identity for a node property / relation identity; a `nested { scope }`
//     envelope for a direct-relation `externalId`/`space` subpath).
//
// Three identity surfaces push down, all via the site machinery:
//   1. base node properties `externalId` / `space` (the view's `property`
//      proxy resolves them to `["node", prop]`);
//   2. a direct-relation-target subpath `(col % rel externalId|space)` — and,
//      equivalently, a bare direct relation compared to externalId STRINGS —
//      fold to a `nested` filter scoped on the relation property;
//   3. a bare direct relation compared to instance REFS folds to a precise,
//      space-aware `equals`/`in` over the relation property.
//
// Adding a new pushable op is one change: add it to the builder table whose
// arity + value domain it shares. It is then pushable and translatable in
// every LHS position that domain admits.

import { type DataModelView, viewToSchema } from '#core/lib/cognite/view.ts';
import { DATE_FORMAT_PATTERN } from '#core/lib/utils/date.ts';
import { isInstanceReference } from '#core/schema/utils.ts';

import { isInternedValue, resolveInternedNode } from '../../interning.ts';
import { type AstInput, astShape, type OpCall } from '../../normalize.ts';
import { rowColLabels } from '../../providers/decode.ts';
import { partitionPredicate } from '../../providers/partition.ts';
import {
  type ColumnType,
  isIdentityBearing,
  isTemporalColumn,
  type Schema,
  unwrapNullable,
} from '../../schema/types.ts';
import { formatDateValue } from '../transform/date-utils.ts';

/**
 * Normalize an `OpCall`'s `params` to a definite array. `OpCall.params`
 * is optional on the type because zero-arg calls omit it; every helper
 * here treats an absent `params` as an empty positional list.
 */
const callParams = (call: OpCall): readonly AstInput[] => (call.params ?? []) as readonly AstInput[];

/** Total-on-own-keys table lookup — the functional `Option` over a record. */
const lookup = <T>(table: Readonly<Record<string, T>>, key: string): T | undefined =>
  Object.hasOwn(table, key) ? table[key] : undefined;

/** Builds the DMS leaf JSON for a binary op from its (coerced) value + resolved property. */
type BinaryBuild = (value: unknown, property: unknown) => unknown;
/** Builds the DMS leaf JSON for a unary op from its resolved property — no value (that IS the arity). */
type UnaryBuild = (property: unknown) => unknown;

/**
 * Equality / membership ops. Valid over BOTH string scalars and opaque
 * instance references — `equals`/`in` compare by value either way, so these
 * are the only ops a direct-relation identity LHS admits.
 */
const equalityLeaf = {
  '=': (value, property) => ({ equals: { property, value } }),
  '!=': (value, property) => ({ not: { equals: { property, value } } }),
  in: (value, property) => ({ in: { property, values: value } }),
} as const satisfies Record<string, BinaryBuild>;

/**
 * Ordered / string-domain ops. Meaningful only over comparable scalars
 * (numbers, dates, strings incl. `externalId`/`space`) — never opaque
 * instance refs.
 */
const orderedLeaf = {
  prefix: (value, property) => ({ prefix: { property, value } }),
  '>': (value, property) => ({ range: { property, gt: value } }),
  '>=': (value, property) => ({ range: { property, gte: value } }),
  '<': (value, property) => ({ range: { property, lt: value } }),
  '<=': (value, property) => ({ range: { property, lte: value } }),
} as const satisfies Record<string, BinaryBuild>;

/**
 * Unary existence ops. `is-null` / `is-not-null` fold to `{not:{exists}}` /
 * `{exists}`. Only a scalar-column site admits them today — a relation site
 * defers (no `nested.exists` wiring yet) via `allowsExistence: false`.
 */
const existenceLeaf = {
  'is-null': (property) => ({ not: { exists: { property } } }),
  'is-not-null': (property) => ({ exists: { property } }),
} as const satisfies Record<string, UnaryBuild>;

/**
 * The SINGLE container-membership emission helper (SSOT): an array / relation
 * LIST property holds ANY of `values` → CDF `{containsAny:{property,values}}`.
 * `values` is always a vector — the array domain's `coerce` lifts a scalar
 * (`contains`'s single element) into a one-element vector before emission, so
 * `contains` and `in` funnel through this one builder with no per-op shape.
 */
const containsAny: BinaryBuild = (value, property) => ({ containsAny: { property, values: value } });

/**
 * Container-membership ops over an array / relation-list column. `contains`
 * (the canonical container op) and `in` (the membership equivalent, including
 * the agent's `(in (col % rel externalId) <set>)` misuse) BOTH fold to the one
 * `containsAny` helper — there is no second array-membership representation.
 */
const arrayLeaf = {
  contains: containsAny,
  in: containsAny,
} as const satisfies Record<string, BinaryBuild>;

// CDF DMS `timestamp` / `date` properties expect ISO-8601 strings. An
// LLM routinely supplies a bare date string (`"2025-01-01"`) or the
// engine a `Date` / epoch-ms; pushing that raw into the instance-query
// filter silently matches nothing (or 400s). Normalize date/timestamp
// literals to the format CDF expects; non-date columns pass through
// byte-for-byte (existing behavior preserved). Reuses the `toDateValue`
// SSOT via `formatDateValue`.
const CDF_TIMESTAMP_PATTERN = 'YYYY-MM-DDTHH:mm:ss.SSSZ';
const coerceCdfValue = (raw: unknown, colType: ColumnType | undefined): unknown => {
  if (colType === undefined || !isTemporalColumn(colType)) {
    return raw;
  }
  const pattern = unwrapNullable(colType).kind === 'date' ? DATE_FORMAT_PATTERN : CDF_TIMESTAMP_PATTERN;
  const norm = (v: unknown): unknown => formatDateValue(v, pattern) ?? v;
  return Array.isArray(raw) ? raw.map(norm) : norm(raw);
};

const isLiteralOrLiteralVector = (node: AstInput): boolean => {
  const shape = astShape(node);
  if (shape.kind === 'embedded') {
    // A scalar or Date literal pushes down; an opaque identity object /
    // Dataset does not (instance refs route through `isInstanceRefOrVector`).
    // CDF DMS cannot filter on null (no `equals`/`in`/`range` accepts a
    // null operand). Reject it here so the leaf routes to residual and
    // is evaluated in-memory with correct JS null semantics.
    const { value } = shape;
    return (
      typeof value === 'number' || typeof value === 'string' || typeof value === 'boolean' || value instanceof Date
    );
  }
  if (shape.kind === 'vector') {
    return shape.elements.every((el) => isLiteralOrLiteralVector(el));
  }
  return false;
};

/**
 * True iff `node` is a CDF instance reference (`{space, externalId}`)
 * or a non-empty vector of them. The RHS shape a direct-relation identity
 * leaf accepts — a `{space, externalId}` object is an opaque `embedded`
 * value (a scalar/Date literal is not), so `isLiteralOrLiteralVector`
 * structurally cannot match it; this predicate selects the identity value
 * domain instead. Reuses the `isInstanceReference` SSOT guard from
 * `cognite/types.ts`.
 */
const isInstanceRefOrVector = (node: AstInput): boolean => {
  if (isInstanceReference(node)) {
    return true;
  }
  // An interned handle stands in for a large identity vector — it is ONLY ever
  // minted for one (`instanceRefSet`), so it selects the identity domain just
  // like an inline vector. The handle resolves back to the real refs before
  // `compileNode` at translate time; at partition time the handle alone settles
  // the domain.
  if (isInternedValue(node)) {
    return true;
  }
  const shape = astShape(node);
  return shape.kind === 'vector' && shape.elements.length > 0 && shape.elements.every((el) => isInstanceReference(el));
};

/**
 * A value domain: the binary op builders it admits, the RHS-shape guard for
 * that domain, and the value coercion applied before emission. Op validity
 * per domain is encoded by which builders the table holds — a missing key IS
 * "this op does not push in this domain".
 */
type Domain = {
  readonly binary: Readonly<Record<string, BinaryBuild>>;
  readonly acceptsRhs: (rhs: AstInput) => boolean;
  readonly coerce: (value: unknown, colType: ColumnType | undefined) => unknown;
};

/** Comparable scalars (incl. `externalId`/`space` strings): equality + ordering, literal RHS. */
const stringDomain: Domain = {
  binary: { ...equalityLeaf, ...orderedLeaf },
  acceptsRhs: isLiteralOrLiteralVector,
  coerce: coerceCdfValue,
};

/** Opaque instance references: equality/membership only, instance-ref RHS, no coercion. */
const identityDomain: Domain = {
  binary: equalityLeaf,
  acceptsRhs: isInstanceRefOrVector,
  coerce: (value) => value,
};

/**
 * Plain (non-relation) array container membership — `tags`/`aliases` →
 * `array<string>`. `contains`/`in` only, via the single `containsAny` helper.
 * `coerce` lifts a single element into a one-element vector so the emitted
 * `values` is always a vector, normalizing `contains`'s scalar arg to the same
 * shape `in` already carries. Literal RHS only — a relation list resolves its
 * own instance-ref values through {@link relationListDomain}.
 */
const arrayDomain: Domain = {
  binary: arrayLeaf,
  acceptsRhs: isLiteralOrLiteralVector,
  coerce: (value) => (Array.isArray(value) ? value : [value]),
};

/**
 * Relation-LIST container membership (`assets`/`children`/…). The set-ref pass
 * resolves the RHS to FULL `{space, externalId}` instance refs (taken from the
 * source's real node identity), so this differs from {@link arrayDomain} only
 * in accepting an instance-ref RHS — the relation target space is never assumed
 * here. A bare externalId-string RHS cannot form an instance ref without a
 * space, so it does not push and falls to residual.
 */
const relationListDomain: Domain = { ...arrayDomain, acceptsRhs: isInstanceRefOrVector };

/**
 * True iff `colType` is a direct relation as surfaced by
 * `zodFieldToColumnType` — a `row-identity` column OR a `hydrated-relation`
 * (both carry CDF instance references; the hydrated one additionally exposes
 * walkable fields). Identity-ness lives in the TYPE KIND and is tested through
 * the single {@link isIdentityBearing} SSOT, never a struct field-name sniff.
 * SSOT for the "is this a direct relation" test, shared by `relationSubpathLeaf`
 * (the `(col ds rel externalId|space)` subpath form) and `directRelationLeaf`
 * (the bare-symbol form).
 */
const isDirectRelationType = (colType: ColumnType | undefined): boolean => isIdentityBearing(colType);

/** The element type of an array column, through a nullable wrapper; `undefined` for a non-array. */
const arrayElementType = (colType: ColumnType | undefined): ColumnType | undefined => {
  const unwrapped = colType === undefined ? undefined : unwrapNullable(colType);
  return unwrapped !== undefined && unwrapped.kind === 'array' ? unwrapNullable(unwrapped.element) : undefined;
};

/**
 * True iff `colType` is a relation LIST — `array<row-identity>` (e.g.
 * `children`/`equipment`/`files`/`assets`). The list counterpart to
 * `isDirectRelationType`; both feed the same `containsAny` membership path.
 */
const isRelationListType = (colType: ColumnType | undefined): boolean =>
  isDirectRelationType(arrayElementType(colType));

/** True iff `colType` is a plain (non-relation) array column, e.g. `tags`/`aliases` → `array<string>`. */
const isArrayColumnType = (colType: ColumnType | undefined): boolean =>
  arrayElementType(colType) !== undefined && !isRelationListType(colType);

/**
 * If `lhs` is a direct-relation-target subpath reference —
 * `(col <ds> <relCol> externalId)` / `(col <ds> <relCol> space)` — and
 * `relCol` resolves on `schema` to a direct relation, return the relation
 * column name + the base subpath (`externalId` / `space`); else `undefined`.
 *
 * The LHS is the canonical row-scoped form `(col % <relCol> <subpath>)`
 * (`rowColLabels` → `[relCol, subpath]`). Only the two base node properties
 * are reachable through a relation — any other subpath has no DMS `nested`
 * shape and routes to residual.
 */
const relationSubpathLeaf = (
  lhsNode: AstInput,
  schema: Schema,
): { readonly relColumn: string; readonly subpath: 'externalId' | 'space' } | undefined => {
  const labels = rowColLabels(lhsNode);
  if (labels === undefined || labels.length !== 2) {
    return undefined;
  }
  const [relColumn, subpath] = labels;
  if (subpath !== 'externalId' && subpath !== 'space') {
    return undefined;
  }
  if (!isDirectRelationType(schema.get(relColumn as string))) {
    return undefined;
  }
  return { relColumn: relColumn as string, subpath };
};

/**
 * If `lhsNode` is a single column of the row (`(col % <relCol>)` or a bare
 * `{$sym}`) whose `schema` type is a direct relation, return the column name;
 * otherwise `undefined`. The no-subpath counterpart to `relationSubpathLeaf`.
 */
const directRelationLeaf = (lhsNode: AstInput, schema: Schema): string | undefined => {
  const labels = rowColLabels(lhsNode);
  if (labels === undefined || labels.length !== 1 || !isDirectRelationType(schema.get(labels[0] as string))) {
    return undefined;
  }
  return labels[0] as string;
};

/**
 * The compilation strategy an LHS resolves to: its DMS property identifier,
 * the value domain it admits, whether unary existence ops apply, and how to
 * wrap the emitted leaf. Dispatch is by USING these fields — there is no
 * discriminant to switch on.
 */
type LeafSite = {
  readonly property: unknown;
  readonly colType: ColumnType | undefined;
  readonly allowsExistence: boolean;
  readonly domain: Domain;
  readonly wrap: (dms: unknown) => unknown;
};

/**
 * Resolve a column's DMS property identifier through the view's `property`
 * proxy: `externalId`/`space` → `["node", prop]`; declared properties →
 * `[space, table, prop]`. Returns `undefined` (⇒ not pushable) when the
 * column is not a view property — never throws, so the same total path backs
 * both pushability and translation.
 */
const resolveProperty = (view: DataModelView, name: string): unknown => {
  try {
    const property = (view.property as Record<string, unknown>)[name];
    return property === null ? undefined : property;
  } catch {
    return undefined;
  }
};

/** A bare scalar column (incl. node `externalId`/`space`): string domain, no wrap, existence ok. */
const scalarSite = (view: DataModelView, column: string, colType: ColumnType | undefined): LeafSite | undefined => {
  const property = resolveProperty(view, column);
  return property === undefined
    ? undefined
    : { property, colType, allowsExistence: true, domain: stringDomain, wrap: (dms) => dms };
};

/** A direct relation compared to instance refs: identity domain over the relation property, no wrap. */
const identitySite = (view: DataModelView, relColumn: string): LeafSite | undefined => {
  const property = resolveProperty(view, relColumn);
  return property === undefined
    ? undefined
    : { property, colType: undefined, allowsExistence: false, domain: identityDomain, wrap: (dms) => dms };
};

/**
 * A direct-relation `externalId`/`space` subpath (explicit, or a bare relation
 * vs externalId strings): string domain over `["node", subpath]`, wrapped in a
 * `nested { scope }` envelope scoped on the relation property.
 */
const nestedSite = (view: DataModelView, relColumn: string, subpath: 'externalId' | 'space'): LeafSite | undefined => {
  const scope = resolveProperty(view, relColumn);
  return scope === undefined
    ? undefined
    : {
        property: ['node', subpath],
        colType: undefined,
        allowsExistence: false,
        domain: stringDomain,
        wrap: (dms) => ({ nested: { scope, filter: dms } }),
      };
};

/**
 * An array / relation-list column (`tags`, `assets`): container membership over
 * the BASE property, no wrap, no existence. Reached by a bare array column and
 * by the `(col % rel externalId)` projection pivot — the projection is dropped
 * because membership is over the relation list itself, not a node subpath. A
 * relation LIST uses {@link relationListDomain} (instance-ref RHS, resolved
 * upstream to the source's real {space, externalId}); a plain string array uses
 * {@link arrayDomain} verbatim.
 */
const arraySite = (view: DataModelView, column: string, isRelationList: boolean): LeafSite | undefined => {
  const property = resolveProperty(view, column);
  return property === undefined
    ? undefined
    : {
        property,
        colType: undefined,
        allowsExistence: false,
        domain: isRelationList ? relationListDomain : arrayDomain,
        wrap: (dms) => dms,
      };
};

/**
 * If `lhs` is the `.externalId` projection of a relation LIST —
 * `(col % <relList> externalId)` over an `array<row-identity>` column —
 * return the base relation-list column name; else `undefined`. This
 * is the agent's `(in (col % assets externalId) <set>)` MISUSE; it pivots onto
 * the base property's `containsAny` membership rather than dropping to residual.
 */
const relationListProjectionLeaf = (lhs: AstInput, schema: Schema): string | undefined => {
  const labels = rowColLabels(lhs);
  if (labels === undefined || labels.length !== 2 || labels[1] !== 'externalId') {
    return undefined;
  }
  return isRelationListType(schema.get(labels[0] as string)) ? (labels[0] as string) : undefined;
};

/**
 * If `lhs` is a bare array / relation-list column (`(col % <col>)` or a bare
 * `{$sym}`), return the column name; else `undefined`. The no-projection
 * counterpart to `relationListProjectionLeaf`.
 */
const arrayColumnLeaf = (lhs: AstInput, schema: Schema): string | undefined => {
  const labels = rowColLabels(lhs);
  if (labels === undefined || labels.length !== 1) {
    return undefined;
  }
  const colType = schema.get(labels[0] as string);
  return isArrayColumnType(colType) || isRelationListType(colType) ? (labels[0] as string) : undefined;
};

/**
 * Classify a leaf's LHS (given its RHS, to distinguish a relation compared to
 * instance refs from one compared to externalId strings) into the site that
 * knows how to compile it, or `undefined` when no surface pushes it.
 */
const leafSite = (
  lhs: AstInput,
  rhs: AstInput | undefined,
  view: DataModelView,
  schema: Schema,
): LeafSite | undefined => {
  const projectedList = relationListProjectionLeaf(lhs, schema);
  if (projectedList !== undefined) {
    return arraySite(view, projectedList, true);
  }
  const arrayColumn = arrayColumnLeaf(lhs, schema);
  if (arrayColumn !== undefined) {
    return arraySite(view, arrayColumn, isRelationListType(schema.get(arrayColumn)));
  }
  const sub = relationSubpathLeaf(lhs, schema);
  if (sub !== undefined) {
    return nestedSite(view, sub.relColumn, sub.subpath);
  }
  const rel = directRelationLeaf(lhs, schema);
  if (rel !== undefined) {
    // Instance-ref RHS → space-aware identity filter; string RHS → match the
    // relation target by externalId (the `nested` subpath form). A bare
    // relation vs a bare string would be a CDF 400, so the string case MUST
    // route through `nested`, never the identity domain.
    return rhs !== undefined && isInstanceRefOrVector(rhs)
      ? identitySite(view, rel)
      : nestedSite(view, rel, 'externalId');
  }
  const labels = rowColLabels(lhs);
  if (labels?.length === 1 && schema.has(labels[0] as string)) {
    const column = labels[0] as string;
    return scalarSite(view, column, schema.get(column));
  }
  return undefined;
};

/**
 * Compile a single leaf call to its CDF filter JSON, or `undefined` when it
 * does not push. The site supplies the property / domain / wrap; the op's
 * builder table supplies the DMS shape. No op names are hard-coded here.
 */
const compileLeaf = (call: OpCall, view: DataModelView, schema: Schema): unknown => {
  const params = callParams(call);
  const lhs = params[0];
  if (lhs === undefined) {
    return undefined;
  }
  const site = leafSite(lhs, params[1], view, schema);
  if (site === undefined) {
    return undefined;
  }

  const unary = lookup(existenceLeaf, call.name);
  if (unary !== undefined) {
    return site.allowsExistence && params.length === 1 ? site.wrap(unary(site.property)) : undefined;
  }

  const build = lookup(site.domain.binary, call.name);
  const rhs = params[1];
  if (build === undefined || params.length !== 2 || rhs === undefined || !site.domain.acceptsRhs(rhs)) {
    return undefined;
  }
  return site.wrap(build(site.domain.coerce(rhs, site.colType), site.property));
};

/**
 * Compile a predicate subtree to CDF filter JSON, or `undefined` when any
 * part of it is not expressible in CDF's filter language. Boolean combinators
 * recurse and combine (`and`/`or` → arrays, `not` → wrapper); everything else
 * is a leaf. This single function is the SSOT for both pushability and
 * translation.
 */
const compileNode = (node: AstInput, view: DataModelView, schema: Schema): unknown => {
  const shape = astShape(node);
  if (shape.kind !== 'opCall') {
    return undefined;
  }
  const { call } = shape;
  const params = callParams(call);
  if (call.name === 'and' || call.name === 'or') {
    const children = params.map((p) => compileNode(p, view, schema));
    return children.some((c) => c === undefined) ? undefined : { [call.name]: children };
  }
  if (call.name === 'not') {
    if (params.length !== 1) {
      return undefined;
    }
    const child = compileNode(params[0] as AstInput, view, schema);
    return child === undefined ? undefined : { not: child };
  }
  return compileLeaf(call, view, schema);
};

/**
 * True iff `node` is fully expressible in CDF's filter language — i.e. it
 * compiles. `schema` is the input dataset schema (a column not on it is a
 * cross-dataset ref or typo and does not push); `view` carries the CDF
 * property metadata the compiler resolves DMS paths through.
 */
export const isPushableNode = (node: AstInput, schema: Schema, view: DataModelView): boolean =>
  compileNode(node, view, schema) !== undefined;

/**
 * Translate a pushable predicate (output of `partitionFilter`'s `pushed`
 * side) to the CDF instance-query filter JSON shape.
 *
 * Caller must pass a node previously declared pushable by `partitionFilter`;
 * a non-compiling input throws `PartitionInvariantError` — a programming
 * error, not a runtime LLM error. The schema is derived from `view` via
 * `viewToSchema` (SSOT), identical to the schema the partitioner tested.
 *
 * Any interned identity handle in the predicate is expanded back to its real
 * `{space, externalId}` vector via `resolveInterned` BEFORE compilation — the
 * handle only ever rode the AST to keep the source cache key small; the emitted
 * CDF filter always carries the full ref set. `resolveInterned` must come from
 * the SAME session that interned the handle (the provider's `fetch` ctx).
 */
export const translatePredicateToCdf = (
  pushed: AstInput,
  view: DataModelView,
  resolveInterned?: (key: string) => unknown,
): unknown => {
  const expanded = resolveInterned === undefined ? pushed : resolveInternedNode(pushed, resolveInterned);
  const dms = compileNode(expanded, view, viewToSchema(view));
  if (dms === undefined) {
    throw new PartitionInvariantError('predicate was partitioned as pushable but did not compile to a CDF filter');
  }
  return dms;
};

/**
 * Split an s-expression predicate into pushable + residual subtrees.
 *
 * Thin CDF binding over the engine-owned shared partitioner
 * (`partitionPredicate`): the boolean and/or/not split + legality live in the
 * shared helper, and CDF supplies the two per-query closures.
 *
 *  - `isPushable` binds the resolved `view` and delegates to `isPushableNode`
 *    (CDF's pushability = "the predicate compiles to a CDF filter").
 *  - `translate` is the IDENTITY here — it returns the assembled pushable
 *    subtree as raw AST, NOT the DMS filter JSON. The CDF view provider calls
 *    `translatePredicateToCdf` on the pushed AST separately (it folds in
 *    `hasData` and projection at query-build time), so this function keeps its
 *    historical `{ pushed, residual }` raw-AST contract.
 *
 * `view` carries the CDF property metadata used to build the DMS `nested`
 * scope path for a direct-relation-target subpath leaf.
 */
export const partitionFilter = (
  predicate: AstInput | undefined,
  schema: Schema,
  view: DataModelView,
): { readonly pushed?: AstInput; readonly residual?: AstInput } => {
  const { native, residual } = partitionPredicate<AstInput>(predicate, schema, {
    isPushable: (node, nodeSchema) => isPushableNode(node, nodeSchema, view),
    translate: (pushed) => pushed,
  });
  return {
    ...(native === undefined ? {} : { pushed: native }),
    ...(residual === undefined ? {} : { residual }),
  };
};

/**
 * Programming error — the partitioner declared a node pushable but it did not
 * compile. The `partitionFilter` / `compileNode` contract guarantees this
 * never fires; surface it as a hard internal error rather than a structured
 * `ExprError` so test failures are obvious.
 */
export class PartitionInvariantError extends Error {
  constructor(message: string) {
    super(`pushdown invariant violation: ${message}`);
    this.name = 'PartitionInvariantError';
  }
}
